import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Truck, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '../utils/seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartPage = () => {
  const { items, loading, updateQuantity, removeItem, total, itemCount } = useCart();
  const navigate = useNavigate();
  const [shippingConfig, setShippingConfig] = useState({ flat_rate: 5.99, free_shipping_threshold: 50 });

  useEffect(() => {
    SEO.applyNoIndex('Shopping Cart | PosterFire', '/cart');
    return () => SEO.resetDefaults();
  }, []);

  useEffect(() => {
    axios.get(`${API}/shipping-config`).then(r => setShippingConfig(r.data)).catch(() => {});
  }, []);

  const shipping = total >= shippingConfig.free_shipping_threshold ? 0 : shippingConfig.flat_rate;
  const grandTotal = total + shipping;
  const untilFree = shippingConfig.free_shipping_threshold - total;

  const handleUpdateQuantity = async (itemId, newQuantity) => {
    try { await updateQuantity(itemId, newQuantity); }
    catch { toast.error('Failed to update quantity'); }
  };

  const handleRemove = async (itemId) => {
    try { await removeItem(itemId); toast.success('Removed from cart'); }
    catch { toast.error('Failed to remove'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 py-12 px-4" data-testid="cart-loading">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-black text-white mb-8">Your Cart</h1>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-gray-900 rounded-xl p-4"><Skeleton className="aspect-[3/4] w-full mb-3" /><Skeleton className="h-4 w-3/4" /></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4" data-testid="cart-empty">
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 mx-auto text-gray-700 mb-6" />
          <h1 className="text-3xl font-black text-white mb-3">Your Cart is Empty</h1>
          <p className="text-gray-500 mb-8">Looks like you haven't picked any posters yet.</p>
          <Link to="/shop" className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold text-sm uppercase tracking-wider rounded-lg hover:from-red-600 hover:to-orange-600 transition-all">
            Start Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 py-8 px-4" data-testid="cart-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-black text-white mb-6">
          Your Cart <span className="text-gray-500 font-normal text-lg">({itemCount} {itemCount === 1 ? 'item' : 'items'})</span>
        </h1>

        {/* Free shipping progress */}
        {untilFree > 0 && (
          <div className="mb-6 bg-gray-900 border border-gray-800 rounded-lg p-4" data-testid="shipping-progress">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Truck className="w-4 h-4" />
                <span>Add <span className="text-orange-400 font-bold">${untilFree.toFixed(2)}</span> more for free shipping</span>
              </div>
              <span className="text-xs text-gray-600">Free over ${shippingConfig.free_shipping_threshold.toFixed(0)}</span>
            </div>
            <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-red-500 to-orange-500 rounded-full transition-all" style={{ width: `${Math.min((total / shippingConfig.free_shipping_threshold) * 100, 100)}%` }} />
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Poster Cards — Side by Side Grid */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="cart-items-grid">
              {items.map((item) => (
                <div key={item.id} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden group" data-testid={`cart-item-${item.id}`}>
                  <Link to={item.product?.slug ? `/products/${item.product.slug}` : `/product/${item.product_id}`} className="block relative">
                    <img
                      src={item.product?.image_url}
                      alt={item.product?.title}
                      className="w-full aspect-[3/4] object-cover"
                      draggable="false"
                      onContextMenu={(e) => e.preventDefault()}
                    />
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemove(item.id); }}
                      className="absolute top-2 right-2 p-1.5 bg-black/70 text-gray-400 hover:text-red-500 hover:bg-black/90 rounded transition-all"
                      data-testid={`remove-item-${item.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </Link>
                  <div className="p-3">
                    <Link to={item.product?.slug ? `/products/${item.product.slug}` : `/product/${item.product_id}`} className="text-xs text-white font-medium line-clamp-2 hover:text-orange-400 transition-colors leading-tight block mb-1.5">
                      {item.product?.title}
                    </Link>
                    <div className="flex items-center justify-between mb-2.5">
                      <span className="text-xs text-yellow-400 font-bold uppercase tracking-wider">{item.size}</span>
                      <span className="text-sm font-bold text-orange-400">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-center gap-0 bg-gray-800 rounded-lg overflow-hidden">
                      <button
                        onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                        className="px-3 py-1.5 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
                        data-testid={`decrease-${item.id}`}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="px-3 py-1.5 text-sm font-bold text-white min-w-[32px] text-center">{item.quantity}</span>
                      <button
                        onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                        className="px-3 py-1.5 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
                        data-testid={`increase-${item.id}`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 sticky top-24" data-testid="order-summary">
              <h2 className="text-lg font-bold text-white mb-5">Order Summary</h2>

              <div className="space-y-3 mb-5 text-sm">
                <div className="flex justify-between text-gray-400">
                  <span>Subtotal ({itemCount} items)</span>
                  <span className="text-white">${total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span className="flex items-center gap-1.5"><Package className="w-3.5 h-3.5" /> Shipping</span>
                  <span className={shipping === 0 ? 'text-green-400 font-semibold' : 'text-white'}>
                    {shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}
                  </span>
                </div>
                <div className="border-t border-gray-800 pt-3 flex justify-between items-center">
                  <span className="text-white font-bold">Total</span>
                  <span className="text-xl font-black text-white">${grandTotal.toFixed(2)}</span>
                </div>
              </div>

              {shipping === 0 && (
                <div className="mb-4 flex items-center gap-2 text-xs text-green-400 bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2">
                  <Truck className="w-3.5 h-3.5" />
                  Free shipping applied!
                </div>
              )}

              <Link
                to="/checkout"
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold text-sm uppercase tracking-wider rounded-lg hover:from-red-600 hover:to-orange-600 transition-all"
                data-testid="checkout-btn"
              >
                Checkout
                <ArrowRight className="w-4 h-4" />
              </Link>

              <Link
                to="/shop"
                className="block text-center text-sm text-gray-500 font-medium mt-4 hover:text-orange-400 transition-colors"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
